/**
 * Round & match flow — 04-combat §6–§8, §10–§11; hub respawn — 10 §1.
 * `beginTick` advances phase timers and returns what the phase permits;
 * `endTick` checks round-end conditions after eliminations were applied.
 */
import * as C from './constants.js'
import { fullBounds } from './arena.js'
import { despawn, dropHeldDiscs } from './disc.js'
import { ALL_ALLOWED, setState, type Permissions } from './player.js'
import { placeForRound, spawnInHub } from './state.js'
import { lobbyFinalize, lobbyReveal } from './lobby.js'
import type { MatchPhase, PendingHit, RoundEndReason, SimState, Team } from './types.js'

const S = (sec: number): number => Math.round(sec * C.SIM_HZ)
const NONE: Permissions = { move: false, dash: false, throw: false, shield: false, jump: false }

export interface TickPermissions extends Permissions {
  /** Players and discs don't step at all. */
  frozen: boolean
  /** Discs can hit / clash (LIVE, OVERTIME, hub). */
  combat: boolean
}

function setPhase(s: SimState, phase: MatchPhase): void {
  s.match.phase = phase
  s.match.phaseTicks = 0
  s.events.push({ type: 'phase', tick: s.tick, phase, roundNumber: s.match.roundNumber, roundIndex: s.match.roundIndex })
}

export function beginTick(s: SimState): TickPermissions {
  const m = s.match
  if (m.paused) return { ...NONE, frozen: true, combat: false }
  if (s.mode === 'hub') {
    hubRespawns(s)
    return { ...ALL_ALLOWED, frozen: false, combat: true }
  }
  m.phaseTicks += 1
  switch (m.phase) {
    case 'LOBBY_PICK':
      if (m.phaseTicks >= S(C.PICK_DECK_S)) { lobbyFinalize(s); setPhase(s, 'LOBBY_LOCK') }
      return { ...NONE, frozen: true, combat: false }
    case 'LOBBY_LOCK':
      if (m.phaseTicks >= S(C.PICK_LOCK_S)) { lobbyFinalize(s); lobbyReveal(s); setPhase(s, 'REVEAL') }
      return { ...NONE, frozen: true, combat: false }
    case 'REVEAL':
      if (m.phaseTicks >= C.REVEAL_TICKS) { setPhase(s, 'RESET'); return beginTick(s) }
      return { ...NONE, frozen: true, combat: false }
    case 'RESET':
      // The lobby runs exactly once per match (06 §4): before round 1, never
      // again — not between rounds, not after a void replay.
      if (s.options.powerups && m.roundNumber === 0 && m.roundResults.length === 0 && Object.keys(m.lobby.locks).length === 0) {
        setPhase(s, 'LOBBY_PICK')
        return { ...NONE, frozen: true, combat: false }
      }
      m.roundNumber += 1
      m.timerTicks = S(C.ROUND_TIME_S)
      m.overtimeTicks = 0
      m.roundTicks = 0
      m.bounds = { 0: fullBounds(s.arena, 0), 1: fullBounds(s.arena, 1) }
      placeForRound(s)
      setPhase(s, 'COUNTDOWN')
      s.events.push({ type: 'countdown', tick: s.tick, n: 3 })
      return { move: C.COUNTDOWN_MOVE_ALLOWED, dash: C.COUNTDOWN_MOVE_ALLOWED, jump: C.COUNTDOWN_MOVE_ALLOWED, throw: false, shield: false, frozen: false, combat: false }
    case 'COUNTDOWN': {
      const third = C.COUNTDOWN_TICKS / 3
      if (m.phaseTicks === Math.round(third)) s.events.push({ type: 'countdown', tick: s.tick, n: 2 })
      else if (m.phaseTicks === Math.round(2 * third)) s.events.push({ type: 'countdown', tick: s.tick, n: 1 })
      if (m.phaseTicks >= C.COUNTDOWN_TICKS) {
        setPhase(s, 'LIVE')
        s.events.push({ type: 'countdown', tick: s.tick, n: 0 })
        return { ...ALL_ALLOWED, frozen: false, combat: true }
      }
      return { move: C.COUNTDOWN_MOVE_ALLOWED, dash: C.COUNTDOWN_MOVE_ALLOWED, jump: C.COUNTDOWN_MOVE_ALLOWED, throw: false, shield: false, frozen: false, combat: false }
    }
    case 'LIVE':
      m.roundTicks += 1
      if (m.timerTicks > 0) m.timerTicks -= 1
      return { ...ALL_ALLOWED, frozen: false, combat: true }
    case 'OVERTIME':
      m.roundTicks += 1
      m.overtimeTicks += 1
      shrinkBounds(s)
      return { ...ALL_ALLOWED, frozen: false, combat: true }
    case 'ROUND_END':
      if (m.phaseTicks >= C.ROUND_END_FREEZE_TICKS) {
        if (m.winner !== null) {
          setPhase(s, 'RESULTS')
          s.events.push({ type: 'matchEnd', tick: s.tick, winner: m.winner, score: [...m.score], coinUsed: m.coinUsed, voidRounds: m.voidRounds })
        } else setPhase(s, 'BETWEEN')
      }
      return { ...NONE, frozen: true, combat: false }
    case 'BETWEEN':
      if (m.phaseTicks >= S(C.BETWEEN_ROUNDS_S)) { setPhase(s, 'RESET'); return beginTick(s) }
      return { move: true, dash: true, jump: true, throw: false, shield: true, frozen: false, combat: false }
    case 'RESULTS':
      if (m.phaseTicks >= S(C.RESULTS_SCREEN_S)) setPhase(s, 'DONE')
      return { ...NONE, frozen: true, combat: false }
    case 'DONE':
      return { ...NONE, frozen: true, combat: false }
  }
}

/** 04 §7: each zone's back edge and both side edges move inward at OT_SHRINK_RATE. */
function shrinkBounds(s: SimState): void {
  const d = C.OT_SHRINK_RATE * C.SIM_DT
  for (const team of [0, 1] as Team[]) {
    const full = fullBounds(s.arena, team)
    const b = s.match.bounds[team]
    const width = b.xMax - b.xMin
    if (width > C.OT_ZONE_MIN_WIDTH) { const step = Math.min(d, (width - C.OT_ZONE_MIN_WIDTH) / 2); b.xMin += step; b.xMax -= step }
    const depth = b.zMax - b.zMin
    if (depth > C.OT_ZONE_MIN_DEPTH) {
      const step = Math.min(d, depth - C.OT_ZONE_MIN_DEPTH)
      if (team === 0) b.zMin += step; else b.zMax -= step
    }
    if (s.match.overtimeTicks % C.SIM_HZ === 0) s.events.push({ type: 'boundsShrink', tick: s.tick, team, xMin: b.xMin, xMax: b.xMax, zMin: b.zMin, zMax: b.zMax })
    void full
  }
}

/** Apply the batched eliminations (04 §4 step 3). Returns victims derezzed. */
export function applyEliminations(s: SimState, hits: PendingHit[]): string[] {
  const victims: string[] = []
  const seen = new Set<string>()
  for (const h of hits) {
    if (seen.has(h.victimId)) continue
    seen.add(h.victimId)
    const v = s.players.find((p) => p.id === h.victimId)
    if (!v || !v.alive) continue
    const attacker = s.players.find((p) => p.id === h.attackerId)
    v.alive = false
    setState(v, 'DEREZ')
    v.vel = { x: 0, y: 0, z: 0 }
    v.roundStats.deaths += 1
    dropHeldDiscs(s, v)
    if (s.mode === 'hub') v.respawnTick = s.tick + S(C.HUB_RESPAWN_S)
    const bank = h.bounceCount >= 1
    if (attacker) {
      attacker.roundStats.eliminations += 1
      if (bank) attacker.roundStats.bankEliminations += 1
    }
    s.events.push({ type: 'hit', tick: s.tick, discId: h.discId, victimId: v.id, attackerId: h.attackerId, bounceCount: h.bounceCount, bank, posthumous: !attacker?.alive })
    s.events.push({ type: 'derez', tick: s.tick, playerId: v.id, attackerId: h.attackerId, discId: h.discId })
    victims.push(v.id)
  }
  return victims
}

function hubRespawns(s: SimState): void {
  for (const p of s.players) {
    if (!p.alive && p.respawnTick > 0 && s.tick >= p.respawnTick) {
      spawnInHub(s, p)
      s.events.push({ type: 'respawn', tick: s.tick, playerId: p.id, invulnUntilTick: p.invulnUntilTick })
    } else if (p.alive && p.invulnUntilTick > 0 && s.tick >= p.invulnUntilTick) {
      p.invulnUntilTick = 0
      s.events.push({ type: 'invulnEnd', tick: s.tick, playerId: p.id })
    }
  }
}

const living = (s: SimState, team: Team): number => s.players.filter((p) => p.team === team && p.alive).length
const sumRound = (s: SimState, team: Team, k: 'eliminations' | 'perfectDeflects'): number => s.players.filter((p) => p.team === team).reduce((a, p) => a + p.roundStats[k], 0)
const sumMatch = (s: SimState, team: Team): number => s.players.filter((p) => p.team === team).reduce((a, p) => a + p.matchStats.eliminations + p.roundStats.eliminations, 0)

/** Deterministic coin (04 §7): hash(matchId, roundIndex) & 1. */
export function coinFlip(matchId: string, roundIndex: number): Team {
  const str = `${matchId}:${roundIndex}`
  let h = 0x811c9dc5
  for (let i = 0; i < str.length; i++) { h ^= str.charCodeAt(i); h = Math.imul(h, 0x01000193) >>> 0 }
  return (h & 1) as Team
}

/** After eliminations: did the round end? (match mode, LIVE/OVERTIME only) */
export function endTick(s: SimState): void {
  if (s.mode !== 'match') return
  const m = s.match
  if (m.phase !== 'LIVE' && m.phase !== 'OVERTIME') return
  const a = living(s, 0), b = living(s, 1)
  if (a === 0 && b === 0) return endRound(s, null, 'double_ko')
  if (a === 0) return endRound(s, 1, 'elimination')
  if (b === 0) return endRound(s, 0, 'elimination')
  if (m.phase === 'LIVE' && m.timerTicks <= 0) {
    if (a !== b) return endRound(s, a > b ? 0 : 1, 'timer_living')
    setPhase(s, 'OVERTIME')
    s.events.push({ type: 'overtime', tick: s.tick })
    return
  }
  if (m.phase === 'OVERTIME' && m.overtimeTicks >= S(C.OVERTIME_S)) {
    if (a !== b) return endRound(s, a > b ? 0 : 1, 'timer_living')
    const re = [sumRound(s, 0, 'eliminations'), sumRound(s, 1, 'eliminations')]
    if (re[0] !== re[1]) return endRound(s, re[0] > re[1] ? 0 : 1, 'ladder_round_elims')
    const pf = [sumRound(s, 0, 'perfectDeflects'), sumRound(s, 1, 'perfectDeflects')]
    if (pf[0] !== pf[1]) return endRound(s, pf[0] > pf[1] ? 0 : 1, 'ladder_perfects')
    const me = [sumMatch(s, 0), sumMatch(s, 1)]
    if (me[0] !== me[1]) return endRound(s, me[0] > me[1] ? 0 : 1, 'ladder_match_elims')
    return endRound(s, null, 'void')
  }
}

export function endRound(s: SimState, winner: Team | null, reason: RoundEndReason): void {
  const m = s.match
  let isVoid = winner === null
  if (isVoid) {
    m.voidRounds += 1
    if (m.voidRounds > C.MAX_VOID_ROUNDS) {
      winner = coinFlip(s.matchId, m.roundIndex)
      m.coinUsed = true
      isVoid = false
      reason = 'coin'
      s.events.push({ type: 'coin', tick: s.tick, roundNumber: m.roundNumber, result: winner })
    }
  }
  if (!isVoid && winner !== null) {
    m.score[winner] += 1
    m.roundIndex += 1
  }
  for (const p of s.players) {
    if (p.alive) p.roundStats.survived += 1
    const ms = p.matchStats, rs = p.roundStats
    for (const k of Object.keys(ms) as (keyof typeof ms)[]) ms[k] += rs[k]
  }
  m.roundResults.push({ roundNumber: m.roundNumber, winner, reason, void: isVoid, durationTicks: m.roundTicks, overtime: m.overtimeTicks > 0 })
  if (!isVoid && winner !== null && m.score[winner] >= C.ROUNDS_TO_WIN) m.winner = winner
  for (const d of s.discs) despawn(s, d)
  s.events.push({ type: 'roundEnd', tick: s.tick, roundNumber: m.roundNumber, winner, reason, void: isVoid, score: [...m.score] })
  setPhase(s, 'ROUND_END')
}

/** Forfeit/abandon (04 §8) — called by the server when seats empty past grace. */
export function forfeit(s: SimState, winner: Team | null): void {
  endEarly(s, winner, 'forfeit', true)
}

/** 10 §7 drain end (sprint-3 ruling): the current score leader wins, a tie is
 *  no result; the score is NOT inflated to ROUNDS_TO_WIN. Called by the
 *  server at a round boundary after the cap, or immediately at the hard cap. */
export function drainEnd(s: SimState): void {
  const [a, b] = s.match.score
  endEarly(s, a === b ? null : a > b ? 0 : 1, 'drained', false)
}

function endEarly(s: SimState, winner: Team | null, reason: 'forfeit' | 'drained', inflateScore: boolean): void {
  const m = s.match
  if (m.phase === 'RESULTS' || m.phase === 'DONE') return
  m.winner = winner
  if (inflateScore && winner !== null) m.score[winner] = C.ROUNDS_TO_WIN
  for (const d of s.discs) despawn(s, d)
  // A drained match records its terminal row so the result carries the reason; a forfeit keeps the played rounds only.
  if (reason === 'drained') m.roundResults.push({ roundNumber: m.roundNumber, winner, reason, void: winner === null, durationTicks: m.roundTicks, overtime: m.overtimeTicks > 0 })
  s.events.push({ type: 'roundEnd', tick: s.tick, roundNumber: m.roundNumber, winner, reason, void: reason === 'drained' && winner === null, score: [...m.score] })
  setPhase(s, 'RESULTS')
  s.events.push({ type: 'matchEnd', tick: s.tick, winner, score: [...m.score], coinUsed: m.coinUsed, voidRounds: m.voidRounds })
}
